import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { isValidPhoneNumber } from 'react-phone-number-input';

import CommentCheckIcon from '../../assets/comment-check-icon';
import { API, ROUTES } from '../../helpers/constants';
import TextInput from '../../components/text-input';
import Dropdown from '../../components/dropdown';
import TextArea from '../../components/text-area';
import FileUpload from '../../components/file-upload';
import { acceptedFileFormat, isMoreThanSize } from '../../helpers/validations';
import useGet from '../../hook/use-get';
import useMutate from '../../hook/use-mutation';
import SummationForm from '../../components/summation-form';
import { getRandomNumber } from '../../helpers/number';
import LoadingListItem from './components/loading-list-item';
import Error from '../../components/error';

const Complaint = () => {
  const navigate = useNavigate();
  const [isComplaintSent, setIsComplaintSent] = useState(false);
  const [securityValue1] = useState(getRandomNumber());
  const [securityValue2] = useState(getRandomNumber());

  const {
    register,
    handleSubmit,
    control,
    formState: { errors }
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit'
  });
  const {
    data: cities,
    isFetching,
    isError,
    refetch
  } = useGet({
    queryKey: 'cities',
    api: API.cities
  });
  const { mutate, isLoading } = useMutate({
    api: API.submitComplaint,
    options: {
      onSuccess: () => {
        setIsComplaintSent(true);
      },
      onError: () => {
        alert('Pengaduan anda gagal dikirim, silahkan coba beberapa saat lagi');
      }
    }
  });

  const onSubmit = (values) => {
    const payload = new FormData();
    payload.append('name', values?.name);
    payload.append('phone', values?.['phone-number']);
    payload.append('city_id', values?.city);
    payload.append('subject', values?.['complaint-title']);
    payload.append('description', values?.['complaint-message']);
    payload.append('file', values?.['photo'][0]);

    mutate(payload);
  };

  const renderContent = () => {
    if (isFetching) {
      return <LoadingListItem />;
    }

    if (isError) {
      return <Error refetch={refetch} />;
    }

    return (
      <>
        <form className="mt-14" onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-6">
            <TextInput
              label="Nama"
              placeholder="Masukan nama anda"
              register={register('name', { required: 'Nama tidak boleh kosong' })}
              error={errors.name}
            />
          </div>
          <div className="mb-6">
            <TextInput
              label="Nomor HP"
              placeholder="Masukan nomor HP anda"
              register={register('phone-number', {
                required: 'Nomor HP tidak boleh kosong',
                validate: {
                  isValidPhoneNumber: (value) => {
                    return isValidPhoneNumber(value, 'ID') || 'Nomor HP tidak valid';
                  }
                }
              })}
              error={errors['phone-number']}
            />
          </div>
          <div className="mb-6">
            <TextInput
              label="Judul Pengaduan"
              placeholder="Masukan Judul Pengaduan"
              register={register('complaint-title', {
                required: 'Judul Pengaduan tidak boleh kosong'
              })}
              error={errors['complaint-title']}
            />
          </div>
          <div className="mb-6">
            <Dropdown
              name="city"
              label="Kota/Kabupaten Kejadian"
              placeholder="Pilih Kota/Kab"
              values={cities?.map((item) => ({
                label: item?.['name'],
                value: item?.id
              }))}
              rules={{ required: true }}
              control={control}
              error={errors.city}
            />
          </div>
          <div className="mb-6">
            <TextArea
              label="Detail Pengaduan"
              rows={3}
              placeholder="Masukan Detail Pengaduan"
              register={register('complaint-message', {
                required: 'Detail Pengaduan tidak boleh kosong'
              })}
              error={errors['complaint-message']}
            />
          </div>
          <div className="mb-6">
            <FileUpload
              label="Foto Peristiwa"
              message="Upload foto dalam format png/jpeg (maks. file 2MB)"
              accept="image/png, image/jpeg"
              register={{
                ...register('photo', {
                  required: 'Foto peristiwa tidak boleh kosong',
                  validate: {
                    lessThan2MB: (files) => {
                      return isMoreThanSize({
                        file: files?.[0],
                        maxSize: 2,
                        message: 'Ukuran foto tidak boleh lebih dari 2MB'
                      });
                    },
                    acceptedFormats: (files) => {
                      return acceptedFileFormat({
                        file: files?.[0],
                        accept: ['image/jpeg', 'image/png'],
                        message: 'Format foto hanya boleh PNG dan JPEG'
                      });
                    }
                  }
                })
              }}
              error={errors['photo']}
            />
          </div>
          <div className="mb-6">
            <SummationForm
              value1={securityValue1}
              value2={securityValue2}
              register={register('answer', {
                required: 'Jawaban tidak boleh kosong',
                validate: (value) => {
                  return Number(value) === securityValue1 + securityValue2 || 'Jawaban salah';
                }
              })}
              error={errors.answer}
            />
          </div>
          <div className="flex justify-end">
            <button
              disabled={isLoading}
              type="submit"
              className="text-black bg-secondary-500 active:bg-secondary-900 font-medium rounded-lg text-sm px-8 py-4 disabled:opacity-60"
            >
              {isLoading ? 'Sedang diproses...' : 'Buat Pengaduan'}
            </button>
          </div>
        </form>
      </>
    );
  };

  return (
    <div className="lg:w-7/12 md:w-10/12 sm:w-11/12 m-auto py-14 px-7">
      {isComplaintSent && (
        <div className="flex justify-center mb-5">
          <CommentCheckIcon size={48} />
        </div>
      )}
      <h1 className="text-2xl md:text-4xl font-bold mb-5">Pengaduan Masyarakat</h1>
      <h4 className="text-base">
        {isComplaintSent
          ? 'Pengaduan anda telah terkirim, terima kasih melakukan pengaduan seputar angkutan dan lalu lintas Jawa Barat'
          : 'Berikan saran, kritik atau pengaduan seputar angkutan dan lalu lintas Jawa Barat'}
      </h4>
      {!isComplaintSent && renderContent()}
      {isComplaintSent && (
        <button
          onClick={() => navigate(ROUTES.home)}
          className="my-6 text-black bg-secondary-500 font-medium rounded-lg text-sm px-8 py-4"
        >
          Kembali ke Home
        </button>
      )}
    </div>
  );
};

export default Complaint;
